import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { DataService, Note } from './data.service';
import { EditSessionService } from './edit-session.service';
import { extractSnapshotContent, snapshotContentEquals } from '../_util/note-snapshot.util';

export interface AbandonedSessionReviewItem {
  noteId: string;
  sessionId: string;
  noteTitle: string;
  openedAt: string;
  lastActiveAt: string;
  currentStateLabel: string;
}

/**
 * Autosave Stage 3 — at app startup, looks for opening snapshots
 * (autosave Stage 2) left behind by edit sessions that never closed
 * normally (crashed tab, closed window, etc), and offers the user a
 * chance to recover or discard them. Never runs automatically beyond
 * this one startup check — restoring a snapshot is always an explicit
 * per-item user choice.
 */
@Injectable({ providedIn: 'root' })
export class AbandonedSessionRecoveryService {
  // Conservative "configurable" retention/abandonment threshold — a
  // snapshot's session must have been inactive at least this long before
  // it's ever considered abandoned. An ordinary page reload refreshes
  // lastActiveAt well inside this window (it's set at snapshot creation and
  // refreshed on every autosave, at most ~25s apart during active editing),
  // so a reload alone can never trigger recovery — only a session that
  // genuinely stopped autosaving (closed tab, crash, etc) can.
  private static readonly ABANDONMENT_THRESHOLD_MS = 5 * 60 * 1000;

  private reviewItemsSubject = new BehaviorSubject<AbandonedSessionReviewItem[]>([]);
  readonly reviewItems$ = this.reviewItemsSubject.asObservable();

  constructor(
    private dataService: DataService,
    private editSessionService: EditSessionService,
  ) {}

  async checkForAbandonedSessions(): Promise<void> {
    try {
      const snapshots = await this.dataService.getAllEditSessionSnapshots();
      if (!snapshots.length) return;

      const candidates: AbandonedSessionReviewItem[] = [];

      for (const snapshot of snapshots) {
        const ageMs = Date.now() - Date.parse(snapshot.lastActiveAt);
        if (!Number.isFinite(ageMs) || ageMs < AbandonedSessionRecoveryService.ABANDONMENT_THRESHOLD_MS) {
          continue; // recently active (including "just reloaded") — not abandoned
        }

        // Being edited live in another tab/window right now? Leave it to that
        // tab's own normal close/autosave lifecycle rather than interfering
        // (also covers "record-lock expiry": a stale live session doesn't count).
        const conflicting = await this.editSessionService.checkForConflictingSession(snapshot.noteId);
        if (conflicting) continue;

        const noteResult = await firstValueFrom(this.dataService.getNote(snapshot.noteId));
        const note = noteResult.status === 'success' ? noteResult.data : undefined;
        if (!note) {
          // Note no longer exists locally — nothing to recover into.
          await this.dataService.deleteEditSessionSnapshot(snapshot.noteId, snapshot.sessionId);
          continue;
        }

        // The current note already holds every autosaved edit made after the
        // snapshot was taken, so it — never the older snapshot — is treated
        // as the primary/default copy; the snapshot is only ever restored on
        // explicit request below.
        const currentContent = extractSnapshotContent(note);
        if (snapshotContentEquals(currentContent, snapshot.content)) {
          // Nothing actually changed during that session — silently clean up.
          await this.dataService.deleteEditSessionSnapshot(snapshot.noteId, snapshot.sessionId);
          continue;
        }

        candidates.push({
          noteId: snapshot.noteId,
          sessionId: snapshot.sessionId,
          noteTitle: note.Title || '(untitled note)',
          openedAt: snapshot.openedAt,
          lastActiveAt: snapshot.lastActiveAt,
          currentStateLabel: this.describeCurrentState(note),
        });
      }

      if (candidates.length > 0) {
        this.reviewItemsSubject.next(candidates);
      }
    } catch (err) {
      console.error('[AbandonedSessionRecovery] Startup check failed', err);
    }
  }

  private describeCurrentState(note: Note): string {
    if (note.syncStatus === 'synced') return 'Saved and synchronized';
    if (note.syncStatus === 'new') return 'Saved locally, not yet created on the server';
    return 'Saved locally, awaiting sync';
  }

  async keepCurrentAndRemove(item: AbandonedSessionReviewItem): Promise<void> {
    await this.dataService.deleteEditSessionSnapshot(item.noteId, item.sessionId);
    this.removeFromList(item);
  }

  /** Restores the opening snapshot's content into the live note as a new,
   * locally-dirty update — it does not touch _rev or sync/version history,
   * matching the same "new restoring update" behavior as Stage 2's discard. */
  async restoreOpeningVersion(item: AbandonedSessionReviewItem): Promise<void> {
    const [noteResult, snapshot] = await Promise.all([
      firstValueFrom(this.dataService.getNote(item.noteId)),
      this.dataService.getEditSessionSnapshot(item.noteId, item.sessionId),
    ]);
    const note = noteResult.status === 'success' ? noteResult.data : undefined;

    if (note && snapshot) {
      const restored: Note = { ...note, ...snapshot.content };
      await this.dataService.autosaveNote(restored);
    }

    await this.dataService.deleteEditSessionSnapshot(item.noteId, item.sessionId);
    this.removeFromList(item);
  }

  /** Leaves the snapshot untouched in IndexedDB; just dismisses this row
   * from the current dialog. Reconsidered again next startup. */
  reviewLater(item: AbandonedSessionReviewItem): void {
    this.removeFromList(item);
  }

  private removeFromList(item: AbandonedSessionReviewItem): void {
    this.reviewItemsSubject.next(
      this.reviewItemsSubject.value.filter((i) => !(i.noteId === item.noteId && i.sessionId === item.sessionId)),
    );
  }
}
